/**
 * Feedback Summary Tab — סיכום משובים
 * All conversation feedback from all clients in one screen with RUD + filter/search
 */

import { useState, useEffect, useCallback } from 'react'
import { Search, Trash2, Edit2, RefreshCw, Star, BookOpen, X, Check } from 'lucide-react'
import api from '../../services/api'

interface Feedback {
  _id: string
  conversationId: any
  clientName?: string
  advisorId: any
  rating: 'good' | 'needs_improvement'
  generalNotes: string
  messageCorrections: any[]
  convertedToRules: string[]
  createdAt: string
  updatedAt: string
}

export default function FeedbackSummaryTab() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filterRating, setFilterRating] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editNotes, setEditNotes] = useState('')
  const [editRating, setEditRating] = useState<'good' | 'needs_improvement'>('good')

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = {}
      if (filterRating) params.rating = filterRating
      if (searchTerm) params.search = searchTerm
      const res = await api.get('/admin/conversation-feedback', { params })
      setFeedbacks(res.data?.feedbacks || [])
      setTotal(res.data?.total || 0)
    } catch {
      setFeedbacks([])
    } finally {
      setLoading(false)
    }
  }, [filterRating, searchTerm])

  useEffect(() => { fetchFeedbacks() }, [fetchFeedbacks])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const handleDelete = async (id: string) => {
    if (!confirm('למחוק את המשוב?')) return
    try {
      await api.delete(`/admin/conversation-feedback/${id}`)
      fetchFeedbacks()
    } catch (err) {
      console.error('Failed to delete feedback:', err)
    }
  }

  const handleStartEdit = (fb: Feedback) => {
    setEditingId(fb._id)
    setEditNotes(fb.generalNotes)
    setEditRating(fb.rating)
  }

  const handleSaveEdit = async (id: string) => {
    try {
      await api.put(`/admin/conversation-feedback/${id}`, {
        rating: editRating,
        generalNotes: editNotes,
      })
      setEditingId(null)
      fetchFeedbacks()
    } catch (err) {
      console.error('Failed to update feedback:', err)
    }
  }

  const handleConvertToRule = async (fb: Feedback) => {
    if (!fb.generalNotes) return
    try {
      await api.post('/admin/advisor-rules', {
        category: 'general',
        title: fb.generalNotes.substring(0, 80),
        content: fb.generalNotes,
        priority: fb.rating === 'needs_improvement' ? 'mandatory' : 'recommended',
      })
      alert('הכלל נוצר בהצלחה! ניתן לערוך אותו בטאב "כללי יועצי"')
    } catch (err) {
      console.error('Failed to convert:', err)
    }
  }

  const formatDate = (d: string) => {
    const date = new Date(d)
    return date.toLocaleDateString('he-IL') + ' ' + date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
  }

  const goodCount = feedbacks.filter(f => f.rating === 'good').length
  const improvementCount = feedbacks.filter(f => f.rating === 'needs_improvement').length

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-bold text-gray-900">סיכום משובים</h2>
          <p className="text-sm text-gray-500 mt-0.5">{total} משובים מכל השיחות</p>
        </div>
        <button onClick={fetchFeedbacks} className="btn btn-outline text-sm p-2" aria-label="רענן">
          <RefreshCw size={16} aria-hidden="true" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card bg-gray-50 text-center py-3">
          <p className="text-2xl font-bold text-gray-800">{total}</p>
          <p className="text-xs text-gray-500">סה"כ</p>
        </div>
        <div className="card bg-green-50 border-green-200 text-center py-3">
          <p className="text-2xl font-bold text-green-700">{goodCount}</p>
          <p className="text-xs text-green-600">תקין</p>
        </div>
        <div className="card bg-orange-50 border-orange-200 text-center py-3">
          <p className="text-2xl font-bold text-orange-700">{improvementCount}</p>
          <p className="text-xs text-orange-600">דורש שיפור</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} aria-hidden="true" />
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="חיפוש בהערות..."
            aria-label="חיפוש בהערות"
            className="input pr-10 w-full"
            style={{ fontSize: '16px' }}
          />
        </div>
        <select
          value={filterRating}
          onChange={e => setFilterRating(e.target.value)}
          aria-label="סינון לפי דירוג"
          className="input text-sm w-40"
        >
          <option value="">כל הדירוגים</option>
          <option value="good">תקין</option>
          <option value="needs_improvement">דורש שיפור</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
      ) : feedbacks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Star size={40} className="mx-auto text-gray-300 mb-3" aria-hidden="true" />
          <p className="text-gray-500">
            {searchTerm || filterRating ? 'לא נמצאו משובים לחיפוש זה' : 'אין משובים עדיין'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {feedbacks.map(fb => {
            const isEditing = editingId === fb._id
            const convTitle = fb.conversationId?.title || 'שיחה'

            return (
              <div key={fb._id} className={`border rounded-xl p-4 ${
                fb.rating === 'needs_improvement' ? 'bg-orange-50/30 border-orange-200' : 'bg-white border-gray-200'
              }`}>
                {/* Top row */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Star size={14} className={fb.rating === 'good' ? 'text-green-500' : 'text-orange-500'} aria-hidden="true" />
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      fb.rating === 'good' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {fb.rating === 'good' ? 'תקין' : 'דורש שיפור'}
                    </span>
                    {fb.clientName && (
                      <span className="text-xs text-gray-500">לקוח: {fb.clientName}</span>
                    )}
                    <span className="text-xs text-gray-400">שיחה: {convTitle}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!isEditing && (
                      <>
                        {fb.generalNotes && (
                          <button onClick={() => handleConvertToRule(fb)} className="p-1.5 hover:bg-blue-50 rounded" title="הפוך לכלל" aria-label="הפוך לכלל">
                            <BookOpen size={15} className="text-primary-500" aria-hidden="true" />
                          </button>
                        )}
                        <button onClick={() => handleStartEdit(fb)} className="p-1.5 hover:bg-gray-100 rounded" title="ערוך" aria-label="ערוך">
                          <Edit2 size={15} className="text-gray-500" aria-hidden="true" />
                        </button>
                        <button onClick={() => handleDelete(fb._id)} className="p-1.5 hover:bg-red-50 rounded" title="מחק" aria-label="מחק">
                          <Trash2 size={15} className="text-red-400" aria-hidden="true" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Content — view or edit */}
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditRating('good')}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border ${
                          editRating === 'good' ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-gray-200 text-gray-500'
                        }`}
                      >תקין</button>
                      <button
                        onClick={() => setEditRating('needs_improvement')}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border ${
                          editRating === 'needs_improvement' ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-white border-gray-200 text-gray-500'
                        }`}
                      >דורש שיפור</button>
                    </div>
                    <textarea
                      value={editNotes}
                      onChange={e => setEditNotes(e.target.value)}
                      aria-label="עריכת הערות משוב"
                      className="input w-full text-sm"
                      rows={3}
                      style={{ fontSize: '16px' }}
                    />
                    <div className="flex gap-2">
                      <button onClick={() => handleSaveEdit(fb._id)} className="btn btn-primary text-xs py-1.5">
                        <Check size={12} aria-hidden="true" /> שמור
                      </button>
                      <button onClick={() => setEditingId(null)} className="btn btn-outline text-xs py-1.5">
                        <X size={12} aria-hidden="true" /> ביטול
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {fb.generalNotes && (
                      <p className="text-sm text-gray-700 leading-relaxed">{fb.generalNotes}</p>
                    )}
                    {fb.messageCorrections?.length > 0 && (
                      <p className="text-xs text-gray-400 mt-1">{fb.messageCorrections.length} תיקוני הודעות</p>
                    )}
                  </>
                )}

                {/* Footer */}
                <p className="text-[11px] text-gray-400 mt-2">{formatDate(fb.createdAt)}</p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
